import {
  ValidationFileCreationFormFields,
  ValidationFileCreationRequestFields,
} from './funds-file-validation.model';
import { SalariesExtensionCreationFormFields } from './funds-salaries-extension.model';
import {
  LiquidationCreationFormFields,
  LiquidationCreationRequestFields,
} from './liquidation.model';
import {
  RegrasCreationFormFields,
  RegrasCreationRequestFields,
} from './molds.model';
import {
  Originator,
  OriginatorCreationRequestFields,
  OriginatorsCreationFormFields,
} from './originator.model';
import { Pagination } from './pagination.model';
import {
  PDDCreationFormFields,
  PDDCreationRequestFields,
} from './pdd-profiles.model';
import { Representative } from './representative.model';
import { SelectData } from './select-input.model';
import { TimeTableCreationFields } from './timetable.model';
import {
  CurrentAccountCreationFormFields,
  CurrentAccountCreationRequestFields,
} from './current-account.model';
import { FormCertifierFund, RequestCertifierFund } from './certifier.model';
import { FundWalletForm, FundWalletRequest } from './fund-wallets.model';
import { FundSlack, FundSlackRequest } from './fund-slack.model';
import { FormGroup } from '@angular/forms';
import { ModelForm } from './form-helpers.model';

export interface Fund {
  id: number;
  nomeFundo: string;
  cnpjFundo: string;
  idGestor: number;
  representantes: Representative[];
  originadores: Originator[];
  status: string;
  iD_FUNDO?: number;
  nome?: string;
  dataFundo?: string;
}

export interface FundPaged extends Pagination {
  fundos: Fund[];
}

export interface FundCreationFields {
  id?: number;
  codigoISIN: string;
  codigoANBID: string;
  dataInicioProcessamento: Date;
  dataFimProcessamento: Date;
  diasUteisCorridos: number;
  prazoFundo: Date;
  aberto: boolean;
  numeroCETIP: string;
  nmrCelic: string;
  dateRegistroCVM: Date;
  numeroSequencialCVM: number;

  perfilPdd: PDDCreationRequestFields[];
  carteiras: FundWalletRequest[];
  originadores: OriginatorCreationRequestFields[];
  representantes: Representative[];
  certificadoras: RequestCertifierFund[];
  slack: FundSlackRequest[];
  assinaturas: FundSignatures;
}

export interface FundFilter {
  data: Date;
  fundos: SelectData[];
  status: SelectData[];
  tipoRecebiveis: SelectData[];
  filtroFundo?: boolean;
  filtroStatus?: boolean;
  filtroRecebiveis?: boolean;
}

export interface FundFilterDialog {
  form: FundFilter;
  fundos: SelectData[];
  status: SelectData[];
  tipoRecebiveis: SelectData[];
}

export interface StatusChange {
  status: string;
  fundos: number[];
}

export interface FundSignatures {
  id: number;
  assinaturaTermoCessaoFundo: boolean;
  gestorAssinaTermoCessao: boolean;
  consultoriaAssinaTermoCessao: boolean;
}

export interface AditionalData {
  nome: string;
  cnpj: string;
  cnpjAdministrador: string;
  cnpjCustodiante: string;
  cnpjGestor: string;
  cnpjAuditorIndependente: string;
  cnpjAgenciaClassRisco: string;
  cnpjEscriturador: string;
  cnpjControladorAtivo: string;
  cnpjControladorPassivo: string;
  cnpjConsultoria: string;

  prazoRecepcaoChaveNfe: string;
  dataAdequacao: string;
  tipoTaxaAtualizada: boolean;
  processarAberturaFecharFundo: boolean;
  prazoRecepcaoLastro: string;
  bloquearCedentes: boolean;
  habilitaWriteOff: boolean;
  habilitaCalculoCCB: boolean;
  habilitaXMLReduzido: boolean;
  ativaImportacaoPedenciaSistemaAtivos: boolean;
  prazoPDD: string;
  habilitaValorizacaoVencidos: boolean;
  numeroSequencialAttTermoCessao: string;
  numeroSequencialAttTermoRecompra: string;
  qtdDiasRetroagirImportacaoPL: number;
  convenio: string;
  verificaContratosC3Registradora: boolean;
  habilitaRating: boolean;
  altaVolumetria: boolean;
  habilitaRoboAssinatura: boolean;
  valorMaximoRoboAssinatura: number;
  enviarNotificacaoEmail: boolean;
  codigoCarteira: string;
  trabalhaUnidadesRecebivel: boolean;
  diluicaoUnidadesRecebivel: boolean;
  considerarPosFixado: boolean;
  autorizaFechamentoFundoAutomatico: boolean;
  operaPLZerado: boolean;
  integrarContabil: boolean;
  apresentaInfoIndexadoresRelEstoque: boolean;
  ordemProcessamento: string;
  gerarEstoqueAposFecharFundo: boolean;
  geraEstoqueProcurador: boolean;
  considerarVencidoDataFechamento: boolean;
  habilitaGerarEstoquePortal: boolean;
  cadastrarCedenteAutomatico: boolean;
  habilitaPDDGlobal: boolean;
  processarIntegracaoSistemaCarteira: boolean;
}

export interface FundsCreationFormFields
  extends FundCreationFields,
    AditionalDataFormFields,
    ValidationFileCreationFormFields {
  tipoFundo: SelectData;
  situacaoAtual: SelectData;
  nivelRisco: SelectData;
  statusProcessamento: SelectData;
  layoutAquisicao: SelectData;
  layoutOcorrencia: SelectData;
  layoutLastro: SelectData;
  layoutCheque: SelectData;
  layout: SelectData;
  codigoLayout: SelectData;
  tipoRecebivel: SelectData[];
  originadoresForm: OriginatorsCreationFormFields[];
  carteirasForm: FundWalletForm[];
  certificadorasForm: FormCertifierFund[];
  perfilPddForm: PDDCreationFormFields[];
  slackForm: FundSlack[];
  regras: RegrasCreationFormFields;
  liquidacao: LiquidationCreationFormFields;
  dadosBancarios: CurrentAccountCreationFormFields;
}

export interface FundsCreationFormFieldsTabs
  extends Omit<
    ModelForm<FundsCreationFormFields>,
    'regras' | 'dadosBancarios' | 'liquidacao' | 'gradeHorarios' | 'prorrogacaoVencimentos' | 'assinaturas'
  > {
  assinaturas: FormGroup<ModelForm<FundSignatures>>;
  regras: FormGroup<ModelForm<RegrasCreationFormFields>>;
  dadosBancarios: FormGroup<ModelForm<CurrentAccountCreationFormFields>>;
  liquidacao: FormGroup<ModelForm<LiquidationCreationFormFields>>;
  gradeHorarios: FormGroup<ModelForm<TimeTableCreationFields>>;
  prorrogacaoVencimentos: FormGroup<ModelForm<SalariesExtensionCreationFormFields>>;
}

export interface AditionalDataFormFields extends AditionalData {
  idAdministrador: SelectData;
  idCustodiante: SelectData;
  idGestor: SelectData;
  idAuditor: SelectData;
  idAgenciaRating: SelectData;
  idEscriturador: SelectData;
  idControladorAtivo: SelectData;
  idControladorPassivo: SelectData;
  idConsultoria: SelectData;
  perfilLiquidacaoSistemaAtivos: SelectData;
}

export interface FundsCreationRequestFields
  extends FundCreationFields,
    AditionalData,
    RegrasCreationRequestFields,
    LiquidationCreationRequestFields,
    ValidationFileCreationRequestFields,
    CurrentAccountCreationRequestFields {
  tipoFundo: string;
  situacaoAtual: string;
  nivelRisco: string;
  statusProcessamento: string;
  layoutAquisicao: string;
  layoutOcorrencia: string;
  layoutLastro: string;
  layoutCheque: string;
  layouts: LayoutSecundario[];
  tipoRecebivel: string;
  idAdministrador: string;
  idCustodiante: string;
  idGestor: string;
  idAuditor: string;
  idAgenciaRating: string;
  idEscriturador: string;
  idControladorAtivo: string;
  idControladorPassivo: string;
  idConsultoria: string;
  perfilLiquidacaoSistemaAtivos: string;
}

export interface LayoutSecundario {
  id?: number;
  idFundo?: number;
  codigoLayout: number;
  layout: number;
}

export interface ReducedFundData {
  id?: string;
  nome: string;
  cnpj: string;
  data: Date;
}

export interface FundClosing {
  id: number;
  cnpj: string;
  nome: string;
  data: string;
  dataAbertura: string;
  ultimoFechamento: string;
  fechamentoAutomatico: boolean;
  status: string;
  idUsuario: number;
  nomeUsuario: string;
  rejeicaoArquivoCobranca: boolean;
  faltandoArquivoCobranca: boolean;
  idFundo?: number;
}

export interface FundClosingPaged extends Pagination {
  fundo: FundClosing[];
}
